export interface PriceEstimation {
  min: number;
  max: number;
  confidence: 'high' | 'medium' | 'low';
}

export function estimateCollaborationPrice(
  followers: number,
  engagementRate: number,
  platform: string,
  contentType: string
): PriceEstimation {
  let basePrice = 0;

  if (followers < 10000) {
    basePrice = 150;
  } else if (followers < 50000) {
    basePrice = 500;
  } else if (followers < 100000) {
    basePrice = 900;
  } else if (followers < 500000) {
    basePrice = 2000;
  } else if (followers < 1000000) {
    basePrice = 5000;
  } else {
    basePrice = 10000;
  }

  let platformMultiplier = 1;
  if (platform === 'YouTube') {
    platformMultiplier = 1.5;
  } else if (platform === 'TikTok') {
    platformMultiplier = 0.8;
  }

  let contentMultiplier = 1;
  if (contentType === 'Video' || contentType === 'Reel') {
    contentMultiplier = 1.3;
  } else if (contentType === 'Story') {
    contentMultiplier = 0.6;
  }

  let engagementMultiplier = 1;
  if (engagementRate >= 10) {
    engagementMultiplier = 1.8;
  } else if (engagementRate >= 7) {
    engagementMultiplier = 1.6;
  } else if (engagementRate >= 5) {
    engagementMultiplier = 1.4;
  } else if (engagementRate >= 3) {
    engagementMultiplier = 1.2;
  } else if (engagementRate >= 2) {
    engagementMultiplier = 1.0;
  } else if (engagementRate >= 1) {
    engagementMultiplier = 0.85;
  } else if (engagementRate >= 0.5) {
    engagementMultiplier = 0.7;
  } else {
    engagementMultiplier = 0.5;
  }

  const estimatedPrice = Math.round(
    basePrice * platformMultiplier * contentMultiplier * engagementMultiplier
  );

  const min = Math.round(estimatedPrice * 0.8);
  const max = Math.round(estimatedPrice * 1.3);

  let confidence: 'high' | 'medium' | 'low' = 'medium';
  if (engagementRate >= 3 && followers >= 10000) {
    confidence = 'high';
  } else if (engagementRate < 1 || followers < 5000 || engagementRate < 0.5) {
    confidence = 'low';
  }

  return { min, max, confidence };
}

export function detectBrandFromCaption(caption: string): string | null {
  if (!caption) return null;

  const brandPatterns = [
    /en partenariat avec @?(\w+)/i,
    /merci à @(\w+)/i,
    /@(\w+) pour/i,
    /avec @(\w+)/i,
    /grâce à @(\w+)/i,
    /offert par @?(\w+)/i,
  ];

  for (const pattern of brandPatterns) {
    const match = caption.match(pattern);
    if (match && match[1]) {
      const brand = match[1].toLowerCase();
      if (brand !== 'ad' && brand !== 'sponsored' && brand.length > 2) {
        return match[1];
      }
    }
  }

  const allMentions = caption.match(/@(\w+)/g);
  if (allMentions && allMentions.length > 0) {
    const collabKeywords = ['collab', 'partnership', 'partenariat', 'sponsor', 'gift'];
    const hasCollabKeyword = collabKeywords.some(keyword =>
      caption.toLowerCase().includes(keyword)
    );

    if (hasCollabKeyword) {
      for (const mention of allMentions) {
        const brandName = mention.substring(1);
        if (brandName.length > 2) {
          return brandName;
        }
      }
    }
  }

  return null;
}

export function isGenericBrandName(brandName: string): boolean {
  if (!brandName) return true;

  const genericTerms = [
    'paid partnership',
    'offert par',
    'code promo',
    'collaboration commerciale',
    'partenariat rémunéré',
    'lien affilié',
    'publicité',
    'marque détectée',
    'marque inconnue',
  ];

  const lowerBrand = brandName.toLowerCase();
  return genericTerms.some(term => lowerBrand.includes(term));
}

export function getContentTypeFromPost(post: any): string {
  if (post.post_type === 'video' || post.post_type === 'reel') {
    return 'Reel';
  } else if (post.post_type === 'story') {
    return 'Story';
  } else if (post.post_type === 'carousel') {
    return 'Carousel';
  }
  return 'Photo';
}
